<template src="./viewChats.html"></template>

<script lang="ts">
    import { defineComponent, nextTick } from 'vue';
    import { HubConnectionState } from '@microsoft/signalr';
    import { axiosRequests } from '../../services/axiosinstance';
    import { editUrl, getFormatedDate, convertToISOString } from '../../services/helper';
    import { Routes } from '../../const';
    import { User } from '../../models/User';
    import { Client } from '../../models/Clients';
    import { Club } from '../../models/Club';
    import { ChatTemplate } from '../../models/ChatTemplate';
    import ChatTemplateDropdown from './ChatTemplateDropdown.vue';

    interface RoomModel {
        roomId: number,
        clientId: number,
        lastName: string,
        firstName: string,
        clientPhoto: string,
        unreadMessageCount: number,
        lastMessage: string,
        isOwner: boolean,
        clubId: number,
        isHide: boolean
    }

    interface MessageModel {
        clientId: number,
        sendDate: string,
        text: string,
        messageId: number,
        isRead: boolean,
        isTechnicalMessage: boolean,
        isClubMessage: boolean,
        isTranslated: boolean
    }

    interface ComponentModel {
        hubConnection: any,
        rooms: RoomModel[],
        selectedRoom: RoomModel | null,
        availableClubs: Club[],
        search: string,
        searchTimeout: any,
        messagesTimeout: any,
        roomsTimeout: any,
        isShowChatBody: boolean,
        currentUser: User | null,
        message: string,
        isTokenRefreshed: boolean,
        connectionInformation: string,
        messages: MessageModel[],
        messageInformation: string,
        isTextareaFocused: boolean,
        textareaHeight: number,
        textarea: any,
        isTemplatesOpen: boolean,
        templates: ChatTemplate[],
        isTemplatesLoading: boolean,
        templatesLoaded: boolean,
        templatesError: boolean,
        templatesReqId: number,
        listScrollTop: number,
        listViewportHeight: number,
        loadedCount: number,
        hasMore: boolean,
        isLoadingPage: boolean,
        searchClientIds: string | null,
        onUpdateHandler: (() => void) | null,
        onReceiveHandler: ((message: any, clientInfo: any, messageRoomId: number) => void) | null
    }

    function escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    // Виртуализация списка чатов: строка фиксированной высоты, рендерим только видимое окно + запас.
    const CHAT_ROW_HEIGHT = 53;
    const CHAT_ROW_OVERSCAN = 8;
    // Серверная пагинация списка чатов
    const CHAT_PAGE_SIZE = 50;

    export default defineComponent({
        name: 'ViewChats',
        components: { ChatTemplateDropdown },
        data(): ComponentModel {
            return {
                hubConnection: null,
                rooms: [],
                selectedRoom: null,
                availableClubs: [],
                search: "",
                searchTimeout: null,
                messagesTimeout: null,
                roomsTimeout: null,
                isShowChatBody: false,
                currentUser: null,
                message: "",
                isTokenRefreshed: false,
                connectionInformation: "",
                messages: [],
                messageInformation: "",
                isTextareaFocused: false,
                textareaHeight: 44,
                textarea: null,
                isTemplatesOpen: false,
                templates: [],
                isTemplatesLoading: false,
                templatesLoaded: false,
                templatesError: false,
                templatesReqId: 0,
                listScrollTop: 0,
                listViewportHeight: 800, // дефолт до замера в mounted, чтобы на первом рендере было достаточно строк
                loadedCount: 0,
                hasMore: false,
                isLoadingPage: false,
                searchClientIds: null,
                onUpdateHandler: null,
                onReceiveHandler: null
            }
        },
        methods: {
            // Клубы для запроса: выбранный клуб, либо все доступные при «Все клубы»
            getClubsParam: function (): string | null {
                let clubs = this.$store.getters.getSelectedClubId;
                if (clubs == "0") clubs = this.availableClubs.map(x => x.id).toString();
                return clubs ? clubs : null;
            },
            buildRoomsPageUrl: function (skip: number, clubs: string): string {
                let url = `${process.env.VUE_APP_CHAT_URL}Chat/GetAdministrationRooms?clubs=${clubs}&skip=${skip}&take=${CHAT_PAGE_SIZE}`;
                if (this.searchClientIds) url += `&clientIds=${this.searchClientIds}`;
                return url;
            },
            // Достраивает RoomModel[] по странице комнат: один батч clients/by-ids только на её клиентов
            buildRoomModels: async function (roomResult: any[]): Promise<RoomModel[]> {
                if (!roomResult || roomResult.length == 0) return [];

                const clients: Client[] = await axiosRequests.post(editUrl(Routes.getClientsByIds), { ids: roomResult.map((x: any) => x.ClientId.toString()) });
                const byId = new Map<number, Client>(clients.map((c: any) => [c.id, c]));

                const result: RoomModel[] = [];
                for (const r of roomResult) {
                    const c = byId.get(r.ClientId);
                    if (!c) continue;
                    result.push({
                        roomId: r.RoomId,
                        clientId: r.ClientId,
                        lastName: c.lastName,
                        firstName: c.firstName,
                        clientPhoto: c.photoUrl,
                        unreadMessageCount: r.UnreadMessageCount,
                        lastMessage: r.LastMessage,
                        isOwner: r.OwnerId == this.currentUser?.id,
                        clubId: r.ClubId,
                        isHide: false
                    });
                }
                return result;
            },
            handleRoomsError: function (err: any) {
                if (err.status == 401) {
                    return;
                } else if (err.status == 500) {
                    this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                } else if (err.status == 403) {
                    this.$router.push('/forbidden');
                } else if (err.status == 400 && err.data) {
                    this.$store.commit('SET_ADD_ERROR', err.data);
                } else {
                    this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                }
            },
            // Сброс и загрузка первой страницы (маунт, смена клуба, старт/сброс поиска)
            getRooms: async function () {
                if (this.availableClubs.length == 0) return;
                const clubs = this.getClubsParam();
                if (!clubs) return;

                this.isLoadingPage = true;
                try {
                    const roomResult = await axiosRequests.get(this.buildRoomsPageUrl(0, clubs));
                    this.rooms = await this.buildRoomModels(roomResult);
                    this.loadedCount = roomResult.length;
                    this.hasMore = roomResult.length == CHAT_PAGE_SIZE;
                    this.resetListScroll();
                } catch (err: any) {
                    this.handleRoomsError(err);
                    return;
                } finally {
                    this.isLoadingPage = false;
                }

                await this.fillViewport();
                await this.selectRoomFromRoute();
            },
            // Догружаем страницы, пока список не заполнит вьюпорт (иначе не будет скролла для догрузки) или пока есть страницы
            fillViewport: async function () {
                let guard = 0;
                while (this.hasMore && guard < 40) {
                    const el = this.$refs.chatsWrapper as HTMLElement | undefined;
                    const viewportH = el ? el.clientHeight : this.listViewportHeight;
                    if (this.totalListHeight > viewportH + CHAT_ROW_HEIGHT * 2) break;
                    await this.loadNextPage();
                    guard++;
                }
            },
            // Выбор комнаты по прямой ссылке /chats/{roomId}: догружаем страницы, пока не найдём (с ограничением)
            selectRoomFromRoute: async function () {
                const routeId = this.$route.params.id;
                if (!routeId || this.selectedRoom?.roomId) return;

                let guard = 0;
                while (guard < 40) {
                    const index = this.rooms.findIndex(x => x.roomId.toString() == routeId.toString());
                    if (index != -1) {
                        this.selectRoom(this.rooms[index], false);
                        return;
                    }
                    if (!this.hasMore) return;
                    await this.loadNextPage();
                    guard++;
                }
            },
            // Догрузка следующей страницы (бесконечный скролл)
            loadNextPage: async function () {
                if (!this.hasMore || this.isLoadingPage) return;
                const clubs = this.getClubsParam();
                if (!clubs) return;

                this.isLoadingPage = true;
                try {
                    const roomResult = await axiosRequests.get(this.buildRoomsPageUrl(this.loadedCount, clubs));
                    const models = await this.buildRoomModels(roomResult);

                    const existing = new Set(this.rooms.map(r => r.roomId));
                    for (const m of models) {
                        if (!existing.has(m.roomId)) this.rooms.push(m);
                    }
                    this.loadedCount += roomResult.length;
                    this.hasMore = roomResult.length == CHAT_PAGE_SIZE;
                } catch (err: any) {
                    this.handleRoomsError(err);
                } finally {
                    this.isLoadingPage = false;
                }
            },
            // SignalR Update: перезабрать только первую страницу и слить в загруженный список (дебаунс)
            refreshFirstPage: function () {
                if (this.roomsTimeout) clearTimeout(this.roomsTimeout);

                this.roomsTimeout = setTimeout(async () => {
                    const clubs = this.getClubsParam();
                    if (!clubs) return;

                    try {
                        const roomResult = await axiosRequests.get(this.buildRoomsPageUrl(0, clubs));
                        const models = await this.buildRoomModels(roomResult);

                        // с конца, чтобы unshift сохранял порядок свежих комнат
                        for (let i = models.length - 1; i >= 0; i--) {
                            const m = models[i];
                            const index = this.rooms.findIndex(r => r.roomId == m.roomId);
                            if (index == -1) {
                                this.rooms.unshift(m);
                            } else {
                                this.rooms[index].unreadMessageCount = m.unreadMessageCount;
                                this.rooms[index].lastMessage = m.lastMessage;
                                this.rooms[index].isOwner = m.isOwner;
                                if (m.unreadMessageCount > 0) {
                                    this.rooms.unshift(...this.rooms.splice(index, 1));
                                }
                            }
                        }
                    } catch (err: any) {
                        this.handleRoomsError(err);
                    }
                }, 1000);
            },
            getAvailableClubs: async function () {
                await axiosRequests.get(editUrl(Routes.getClubs))
                    .then(
                        (data: Club[]) => {
                            this.availableClubs = data;
                        }
                    )
                    .catch(
                        async (err: any) => {
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    );
            },
            selectRoom: function (room: any, isNeedToUpdateRoute: boolean = true) {
                if (isNeedToUpdateRoute) this.$router.push(`/chats/${room.roomId}`);

                this.isShowChatBody = true;

                if (this.selectedRoom?.roomId == room.roomId) return;

                this.selectedRoom = room;

                // войти в SignalR-группу комнаты (могла быть создана уже после нашего подключения к хабу)
                this.hubConnection?.invoke("EnterRoom", room.roomId.toString()).catch(() => {});

                this.loadTemplates();

                this.messages = [];

                this.message = "";

                nextTick(() => this.changeTextArea());

                this.messageInformation = 'chat.load_message';

                if (this.messagesTimeout) clearTimeout(this.messagesTimeout);

                this.messagesTimeout = setTimeout(() => this.getMessages(), 1000);
            },
            onListScroll: function (e: Event) {
                const el = e.target as HTMLElement;
                this.listScrollTop = el.scrollTop;
                // у нижней границы загруженного — подгружаем следующую страницу
                if (el.scrollHeight - el.scrollTop - el.clientHeight < CHAT_ROW_HEIGHT * 10) {
                    this.loadNextPage();
                }
            },
            measureListViewport: function () {
                const el = this.$refs.chatsWrapper as HTMLElement | undefined;
                if (el) this.listViewportHeight = el.clientHeight;
            },
            resetListScroll: function () {
                this.listScrollTop = 0;
                const el = this.$refs.chatsWrapper as HTMLElement | undefined;
                if (el) el.scrollTop = 0;
            },
            // Поиск (вариант A): ищем клиентов по имени в PFMS_DB, их Id передаём в хранимку чатов как фильтр
            searchChat: function () {
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout);
                }

                this.searchTimeout = setTimeout(async () => {
                    const query = (this.search || "").trim();

                    if (query.length == 0) {
                        // выход из режима поиска → обычный список
                        if (this.searchClientIds !== null) {
                            this.searchClientIds = null;
                            await this.getRooms();
                        }
                        return;
                    }

                    if (query.length < 3) return;

                    try {
                        const clients: Client[] = await axiosRequests.get(editUrl(Routes.findClients + "?textToSearch=" + encodeURIComponent(query)));
                        // ограничиваем число id, чтобы query-строка не превысила лимит URL; при >200 совпадений просят уточнить запрос
                        const ids = (clients || []).slice(0, 200).map((c: any) => c.id).join(",");
                        // "0" — заведомо пустой результат (нет комнаты с таким clientId), чтобы показать «ничего не найдено»
                        this.searchClientIds = ids.length > 0 ? ids : "0";
                        await this.getRooms();
                    } catch (err: any) {
                        this.handleRoomsError(err);
                    }
                }, 600)
            },
            redirectToClient: function () {
                if (!this.selectedRoom?.clientId) return;

                this.$store.commit('SET_LAST_VIEWED_CLIENT', { fi: "", id: this.selectedRoom?.clientId, buttonText: 'back_button.chat', buttonPath: `/chats/${this.selectedRoom?.roomId}`, isNeedTranslatedButtonText: true });

                this.$router.push('/client/' + this.selectedRoom?.clientId);
            },
            getMessages: function () {
                this.messages = [];
                axiosRequests.get(`${process.env.VUE_APP_CHAT_URL}Chat/GetAdministrationMessages?roomId=${this.selectedRoom?.roomId}&clubId=${this.selectedRoom?.clubId}`)
                    .then(
                        (data: any[]) => {
                            this.messageInformation = "";

                            let unreadMessageIds: string[] = [];
                            let technicalMessageDate: Date | null = null;
                            let todayDate: Date = new Date();

                            for (let i = 0; i < data.length; i++) {
                                let messageDate = new Date(Date.UTC(data[i].DateCreateUTC.year, data[i].DateCreateUTC.month - 1, data[i].DateCreateUTC.day, data[i].DateCreateUTC.hour, data[i].DateCreateUTC.minute, 0, 0));

                                if (technicalMessageDate == null || technicalMessageDate.toDateString() != messageDate.toDateString()) {
                                    this.messages.push({
                                        clientId: 0,
                                        sendDate: todayDate.toDateString(),
                                        text: todayDate.toDateString() != messageDate.toDateString() ? messageDate.toLocaleDateString() :'chat.today',
                                        messageId: 0,
                                        isTranslated: todayDate.toDateString() == messageDate.toDateString(),
                                        isRead: true,
                                        isTechnicalMessage: true,
                                        isClubMessage: false
                                    });

                                    technicalMessageDate = messageDate;
                                }

                                this.messages.push({
                                    clientId: data[i].ClientId,
                                    sendDate: getFormatedDate(convertToISOString(messageDate), "HH:mm"),
                                    text: escapeHtml(data[i].Message.toString()).replaceAll('\n', '<br>'),
                                    messageId: data[i].MessageId,
                                    isTranslated: false,
                                    isRead: data[i].Read,
                                    isTechnicalMessage: data[i].TechnicalMessage,
                                    isClubMessage: data[i].ClientId == 0 || data[i].ClientId == null
                                });

                                if (!data[i].Read) unreadMessageIds.push(data[i].MessageId);
                            }

                            nextTick(() => this.scroll());

                            if (this.selectedRoom?.isOwner && unreadMessageIds.length > 0) {
                                this.hubConnection.invoke("MarkAsReadAdministrator", this.selectedRoom?.roomId.toString(), unreadMessageIds).catch(() => {});
                            }
                        }
                    )
                    .catch(
                        async (err: any) => {
                            this.messageInformation = "";
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    );
            },
            sendMessage: async function () {
                const trimmedMessage = this.message?.trim();
                if (!trimmedMessage || !this.selectedRoom || !this.selectedRoom.isOwner) return;

                if (!this.hubConnection || this.hubConnection.state !== HubConnectionState.Connected) {
                    this.$store.commit('SET_ADD_ERROR', 'chat.send_failed');
                    return;
                }

                try {
                    await this.hubConnection.invoke("SendAdministration", trimmedMessage, this.selectedRoom.roomId.toString());
                    this.message = "";
                    nextTick(() => this.changeTextArea());
                } catch {
                    this.$store.commit('SET_ADD_ERROR', 'chat.send_failed');
                }
            },
            connectToChat: function () {
                this.hubConnection = this.$store.getters.getHubConnection;

                if (!this.hubConnection) {
                    this.connectionInformation = 'chat.conneting';
                    return;
                }

                this.connectionInformation = "";

                if (this.onUpdateHandler) this.hubConnection.off("Update", this.onUpdateHandler);
                if (this.onReceiveHandler) this.hubConnection.off("Receive", this.onReceiveHandler);

                this.onUpdateHandler = this.refreshFirstPage;
                this.onReceiveHandler = (message: any, clientInfo: any, messageRoomId: number) => this.receiveMessage(message, clientInfo, messageRoomId);

                this.hubConnection.on("Update", this.onUpdateHandler);
                this.hubConnection.on("Receive", this.onReceiveHandler);
            },
            receiveMessage: function (message: any, clientInfo: any, messageRoomId: number) {
                if (messageRoomId == this.selectedRoom?.roomId) {

                    let todayDate: Date = new Date();

                    for (let i = this.messages.length - 1; i >= 0; i--) {
                        if (!this.messages[i].isTechnicalMessage) continue;

                        if (this.messages[i].sendDate == todayDate.toDateString()) break;

                        this.messages.push({
                            clientId: 0,
                            sendDate: todayDate.toDateString(),
                            text: 'chat.today',
                            messageId: 0,
                            isTranslated: true,
                            isRead: true,
                            isTechnicalMessage: true,
                            isClubMessage: false
                        });
                    }

                    this.messages.push({
                        clientId: clientInfo.clientId,
                        sendDate: getFormatedDate(convertToISOString(new Date()), "HH:mm"),
                        text: escapeHtml(message.message.toString()).replaceAll('\n', '<br>'),
                        messageId: message.messageId,
                        isTranslated: false,
                        isRead: true,
                        isTechnicalMessage: false,
                        isClubMessage: clientInfo.clientId == 0 || clientInfo.clientId == null
                    });

                    nextTick(() => this.scroll());

                    this.hubConnection.invoke("MarkAsReadAdministrator", this.selectedRoom?.roomId.toString(), [message.messageId]).catch(() => {});
                } else {
                    this.refreshFirstPage();
                }
            },
            assignAsOwner: async function () {
                await axiosRequests.post(`${process.env.VUE_APP_CHAT_URL}Chat/AssignAsOwner`, { roomId: this.selectedRoom?.roomId, isAdmin: true})
                    .then(
                        () => {
                            if (this.selectedRoom != null) this.selectedRoom.isOwner = true;
                            this.hubConnection.invoke("SendUpdate", this.selectedRoom?.roomId.toString()).catch(() => {});

                            this.hubConnection.invoke("MarkAsReadAdministrator", this.selectedRoom?.roomId.toString(), this.messages.filter(x => x.isRead == false).map(x => x.messageId)).catch(() => {});
                        }
                    )
                    .catch(
                        async (err: any) => {
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 500) {
                                this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                            } else if (err.status == 403) {
                                 this.$router.push('/forbidden');
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    );
            },
            removeOwner: async function () {
                await axiosRequests.post(`${process.env.VUE_APP_CHAT_URL}Chat/RemoveOwner`, { roomId: this.selectedRoom?.roomId })
                    .then(
                        () => {
                            if (this.selectedRoom != null) this.selectedRoom.isOwner = false;
                            this.hubConnection.invoke("SendUpdate", this.selectedRoom?.roomId.toString()).catch(() => {});
                        }
                    )
                    .catch(
                        async (err: any) => {
                            if (err.status == 401) {
                                return;
                            } else if (err.status == 500) {
                                this.$store.commit('SET_ISSHOWINTERNALSERVERERROR', true);
                            } else if (err.status == 403) {
                                this.$router.push('/forbidden');
                            } else if (err.status == 400 && err.data) {
                                this.$store.commit('SET_ADD_ERROR', err.data);
                            } else {
                                this.$store.commit('SET_ADD_ERROR', 'error.something_went_wrong');
                            }
                        }
                    );
            },
            scroll: function () {
                let element = document.getElementById("messages");
                if (!element) return;

                element.scrollTo({
                    top: element.getBoundingClientRect().bottom + 10000000,
                    left: 0,
                    behavior: "smooth",
                });
            },
            updateTextarea: function (e: any) {
                if (e.keyCode == 13 && !e.shiftKey && !e.ctrlKey) {
                    this.sendMessage();
                    e.preventDefault();
                    return;
                }
            },
            changeTextArea: function () {
                if (!this.textarea) this.textarea = document.getElementById("messageTextarea");

                this.textarea.style.height = '44px';
                if (this.textarea.scrollHeight < 44) {
                    this.textareaHeight = 44;
                    this.textarea.style.height = '44px';
                    return;
                }

                if (this.textarea.scrollHeight <= 145) {
                    this.textareaHeight = (this.textarea.scrollHeight);
                } else {
                    this.textareaHeight = 145;
                }

                this.textarea.style.height = `${this.textarea.scrollHeight}px`;
            },
            loadTemplates: async function () {
                const clubId = this.selectedRoom?.clubId;
                if (!clubId) return;

                const reqId = ++this.templatesReqId;
                this.templates = [];
                this.templatesLoaded = false;
                this.templatesError = false;
                this.isTemplatesLoading = true;

                try {
                    const data = await axiosRequests.get(editUrl(Routes.getChatMessageTemplates) + '?clubId=' + clubId);
                    if (reqId !== this.templatesReqId) return; // пришёл устаревший ответ — игнорируем
                    this.templates = (data ?? []).map((t: any) => ({ id: t.id, text: t.text }));
                    this.templatesLoaded = true;
                } catch {
                    if (reqId !== this.templatesReqId) return;
                    this.templatesError = true;
                    this.templates = [];
                } finally {
                    if (reqId === this.templatesReqId) this.isTemplatesLoading = false;
                }
            },
            toggleTemplates: function () {
                this.isTemplatesOpen = !this.isTemplatesOpen;
            },
            selectTemplate: function (template: ChatTemplate) {
                this.message = template.text;
                this.isTemplatesOpen = false;
                nextTick(() => {
                    this.changeTextArea();
                    const el = document.getElementById("messageTextarea") as HTMLTextAreaElement | null;
                    if (el) {
                        el.focus();
                        el.selectionStart = el.selectionEnd = el.value.length;
                    }
                });
            }
        },
         watch: {
             'isChatReconnecting': function () {
                 if (this.isChatReconnecting){
                     this.connectionInformation = 'chat.conneting';
                     this.textareaHeight = 44; 
                    return;
                 }

                 if (!this.isChatReconnecting && this.isChatConnected) {
                     this.connectToChat();

                     nextTick(() => this.changeTextArea());
                     
                     return;
                 }
             },
             'isChatConnected': function () {
                 if (!this.isChatReconnecting && !this.isChatConnected) {
                     this.connectionInformation = 'chat.conneting_error';
                     this.textareaHeight = 44; 
                     return;
                 }

                 if (!this.isChatReconnecting && this.isChatConnected) {
                     this.connectToChat();

                     nextTick(() => this.changeTextArea());

                     return;
                 }
            },
         },
         computed: {
             isChatReconnecting: function () {
                 return this.$store.getters.getIsChatReconnecting;
             },
             isChatConnected: function () {
                 return this.$store.getters.getIsChatConnected;
             },
             showTemplatesButton: function (): boolean {
                 // прячем кнопку, только если точно знаем, что у клуба нет шаблонов.
                 // приведение к ComponentModel — обходит вырождение типа this в computed этого крупного компонента
                 const vm = this as unknown as ComponentModel;
                 return !(vm.templatesLoaded && !vm.templatesError && vm.templates.length === 0);
             },
             // видимые (не скрытые поиском) комнаты — основа виртуализации
             filteredRooms: function (): RoomModel[] {
                 const vm = this as unknown as ComponentModel;
                 return vm.rooms.filter(r => !r.isHide);
             },
             totalListHeight: function (): number {
                 return (this as any).filteredRooms.length * CHAT_ROW_HEIGHT;
             },
             windowStartIndex: function (): number {
                 const vm = this as unknown as ComponentModel;
                 return Math.max(0, Math.floor(vm.listScrollTop / CHAT_ROW_HEIGHT) - CHAT_ROW_OVERSCAN);
             },
             windowedRooms: function (): RoomModel[] {
                 const vm = this as unknown as ComponentModel;
                 const list: RoomModel[] = (this as any).filteredRooms;
                 const start: number = (this as any).windowStartIndex;
                 const end = Math.min(
                     list.length,
                     Math.ceil((vm.listScrollTop + vm.listViewportHeight) / CHAT_ROW_HEIGHT) + CHAT_ROW_OVERSCAN
                 );
                 return list.slice(start, end);
             },
             windowOffsetY: function (): number {
                 return (this as any).windowStartIndex * CHAT_ROW_HEIGHT;
             },
         },
        async beforeMount() {
            this.currentUser = this.$store.getters.getCurrentUser;

            await this.getAvailableClubs();
            await this.connectToChat();

            await this.getRooms();
        },
        mounted() {
            nextTick(() => this.measureListViewport());
            window.addEventListener('resize', this.measureListViewport);
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.measureListViewport);
            if (this.onUpdateHandler) this.hubConnection?.off("Update", this.onUpdateHandler);
            if (this.onReceiveHandler) this.hubConnection?.off("Receive", this.onReceiveHandler);
        }
    });
</script>

<style scoped>
    .chat_page_wrapper {
        height: calc(100vh - 140px);
        overflow: hidden;
        display: flex;
    }

    .left_side {
        width: 400px;
        display: flex;
        flex-flow: column;
    }

    .search_input {
        margin-bottom: 8px;
    }

    .chats_wrapper {
        width: 100%;
        display: block;
        overflow-y: auto;
        overflow-x: hidden;
        height: calc(100% - 44px);
    }

    /* виртуализация: распорка на всю высоту списка + окно с видимыми строками */
    .chats_viewport {
        position: relative;
        width: 100%;
    }

    .chats_window {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
    }

    .chat_wrapper {
        width: calc(100% - 20px);
        display: flex;
        cursor: pointer;
        border-radius: 12px;
        padding: 8px;
        flex-flow: wrap;
        margin-right: 4px;
    }

    .chat_wrapper_active {
        background-color: #c7e9f3;
    }

    .client_photo {
        background-size: cover;
        width: 36px;
        height: 36px;
        border: 1px solid white;
        border-radius: 50%;
        background-position: center center;
        margin-right: 12px;
    }

    .chat_data_wrapper {
        border-bottom: 1.5px solid #d5d5d5;
        padding-bottom: 5px;
        display: flex;
        flex-flow: wrap;
        align-items: center;
        width: calc(100% - 50px);
    }

    .chat_data_wrapper_active {
        border-bottom: none;
        padding-bottom: 2px;
    }

    .chat_info_wrapper {
        width: 100%;
    }

    .chat_info_wrapper_with_uread_messages {
        width: calc(100% - 49px);
    }

    .chat_info_name {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .chat_info_message {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        color: #65656F;
        font-size: 12px;
        line-height: 18px;
    }

    .unread_messages_count {
        background-color: #F7C9E4;
        border-radius: 12px;
        padding: 3px 10px 4px 10px;
        margin-left: 12px;
        font-size: 13px;
        color: #AB0064;
        height: 14px;
        display: flex;
    }

    .unread_messages_count p{
        width: 17px;
        text-align: center;
    }

    .right_side {
        width: 100%;
        margin-left: 12px;
        overflow: hidden;
    }

    .chat_body_wrapper {
        border-radius: 12px;
        border: 1.5px solid #d5d5d5;
        overflow: hidden;
        height: calc(100% - 3px);
    }

    .chat_body_top_wrapper {
        height: 60px;
        background-color: #f6f7f8;
    }

    .chat_body {
        height: calc(100%);
        overflow: auto;
        position: relative;
        padding: 0px 12px;
        overflow-x: hidden;
    }

    .button_back{
        display: none;
    }

    .chat_body_top{
        display: flex;
    }

    .button_exit {
        width: 190px;
        height: 36px;
        padding: 8px 12px;
        border-radius: 12px;
        background-color: #fbe0e0;
        border: none;
        cursor: pointer;
        display: flex;
        justify-content: center;
        font-size: 16px;
        margin-top: 10px;
    }

    .button_exit_img {
        margin-right: 0px;
        margin-top: 1px;
        margin-left: 5px;
        height: 16px;
    }

    .button_owner {
        width: 190px;
        height: 36px;
        padding: 8px 12px;
        border-radius: 12px;
        background-color: #21a7d1;
        border: none;
        cursor: pointer;
        display: flex;
        justify-content: center;
        font-size: 16px;
        margin-top: 10px;
        color: white;
    }

    .button_owner_img {
        margin-right: 0px;
        margin-top: 1px;
        margin-left: 5px;
        height: 16px;
    }

    .client_photo_body_top {
        background-size: cover;
        margin-top: 9px;
        margin-left: 12px;
        width: 41px;
        height: 41px;
        border: 1px solid white;
        border-radius: 50%;
        background-position: center center;
        margin-right: 12px;
    }

    .client_name_body_top {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        cursor: pointer;
        font-weight: bold;
        margin-top: 18px;
        font-size: 18px;
        text-decoration: underline;
        margin-right: 12px;
        width: calc(100% - 285px);
    }

    .button_exit_mobile{
        display: none;
    }

    .button_owner_mobile {
        display: none;
    }

    .right_side_buttons {
        display: flex;
        justify-content: space-between;
    }


    .template_button {
        padding: 8px 12px;
        border-radius: 12px;
        background-color: #f6f7f8;
        border: 1.5px solid #D5D5D5;
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 46px;
        width: 46px;
        margin-top: 18px;
        margin-left: 12px;
        flex-shrink: 0;
        box-sizing: border-box;
        transition: border-color 150ms ease, background-color 150ms ease;
    }

    .template_button:hover {
        border-color: #21A7D1;
    }

    .template_button_active {
        border-color: #21A7D1;
        background-color: #e8f7fb;
    }

    .template_button_img {
        height: 22px;
        width: 22px;
    }

    .message_send_button {
        padding: 8px 12px;
        border-radius: 12px;
        background-color: #21a7d1;
        border: none;
        cursor: pointer;
        display: flex;
        justify-content: center;
        height: 46px;
        width: 46px;
        margin-top: 18px;
    }

    .message_send_button_img {
        margin-right: 0px;
        margin-top: 4px;
        margin-left: 0px;
        height: 22px;
    }

    .info_text {
        font-weight: bold;
        margin-top: 0px;
        margin-bottom: 0px;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 100%;
        text-align: center;
    }

    .technical_message {
        border-radius: 16px;
        background-color: #f6f7f8;
        color: #65656F;
        display: flex;
        width: fit-content;
        padding: 8px 16px;
        margin: 18px auto;
    }

    .club_message {
        border-radius: 16px;
        background-color: #f6f7f8;
        color: black;
        display: flex;
        padding: 20px 16px;
        margin: 6px 12px;
        position: relative;
        width: fit-content;
        min-width: 150px;
        margin-inline: auto 0px;
    }

    .client_message {
        border-radius: 16px;
        background-color: #c7e9f3;
        color: black;
        display: flex;
        padding: 20px 16px;
        margin: 6px 0px;
        position: relative;
        width: fit-content;
        min-width: 150px;
    }

    .message_date {
        margin: 0;
        position: absolute;
        font-size: 12px;
        bottom: 5px;
        right: 16px;
    }

    .chat_content_wrapper {
        height: calc(100% - 60px);
        overflow: hidden;
        display: flex;
        flex-flow: column;
        width: 100%;
    }

    .chat_body_footer_wrapper {
        background-color: #f6f7f8;
        position: relative;
        height: fit-content;
        display: flex;
        width: 100%;
    }

    .chat_body_footer {
        display: flex;
        width: 100%;
        height: fit-content;
    }

    .textarea_wrapper {
        overflow: hidden;
        border-radius: 12px;
        border: 1.5px solid #D5D5D5;
        margin-left: 12px;
        margin-right: 12px;
        margin-top: 18px;
        margin-bottom: 18px;
        width: calc(100% - 143px) !important;
        display: flex;
    }

    /* когда кнопка шаблонов скрыта (у клуба нет шаблонов) — занимаем освободившееся место */
    .textarea_wrapper.no-template-button {
        width: calc(100% - 85px) !important;
    }

    .active {
        border: 1.5px solid #21A7D1 !important;
    }

    .textarea {
        border: unset;
        padding: 11px 12px;
        font-size: 16px;
        width:100%;
        height: 44px;
        resize: none;
        box-sizing: border-box;
        max-height: 145px;
    }

    .textarea:focus-visible {
        outline: unset;
        border: unset;
    }


    @media(max-width: 1200px) {
        .left_side {
            width: 320px;
        }
    }

    @media(max-width: 1000px) {
        .button_exit {
            width: 45px;
        }

        .button_text {
            display: none;
        }

        .button_exit_img {
            margin-right: 7px;
            margin-top: 1px;
            margin-left: 10px;
            height: 16px;
        }

        .button_owner {
            width: 45px;
        }

        .button_owner_img {
            margin-right: 7px;
            margin-top: 1px;
            margin-left: 10px;
            height: 16px;
        }

        .client_name_body_top {
            width: calc(100% - 137px);
        }
    }

    @media(max-width: 768px) {
        .chat_page_wrapper {
            height: calc(100vh - 130px);
        }

        .button_text{
            display: none;
        }
    }

    @media(max-width: 700px) {
        .left_side {
            width: 100%;
        }

        .right_side {
            display: none;
            margin-left: 0px;
        }

        .mobile_hide{
            display: none;
        }

        .mobile_show {
            display: block;
        }

        .button_back {
            width: 35px;
            margin-bottom: 12px;
            padding: 8px 12px;
            border-radius: 12px;
            background-color: #eaebec;
            border: none;
            cursor: pointer;
            display: flex;
            justify-content: center;
        }

        .back_button_img {
            margin-right: 7px;
            margin-top: 1px;
            margin-left: 5px
        }

        .chat_body_wrapper {
            height: calc(100% - 44px);
        }

        .button_exit_mobile {
            height: 29px;
            padding: 6px 12px;
            border-radius: 12px;
            background-color: #fbe0e0;
            border: none;
            cursor: pointer;
            display: flex;
            justify-content: center;
            font-size: 16px;
            width: 180px;
        }

        .client_name_body_top {
            width: calc(100% - 80px);
        }

        .button_exit{
            display: none;
        }

        .button_owner {
            display: none;
        }

        .button_owner_mobile {
            height: 29px;
            padding: 6px 12px;
            border-radius: 12px;
            background-color: #21a7d1;
            border: none;
            cursor: pointer;
            display: flex;
            justify-content: center;
            font-size: 16px;
            width: 210px;
            color: white;
        }
    }

    @media(max-width: 500px) {
        .technical_message {
            font-size: 12px;
        }

        .club_message {
            padding: 16px 16px;
            font-size: 14px;
        }

        .client_message {
            padding: 16px 16px;
            font-size: 14px;
        }
    }
</style>